import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const STATUS_FILTERS = [
  { id: '', labelKey: 'adminKycAllFilter' },
  { id: 'pending', labelKey: 'statusPending' },
  { id: 'approved', labelKey: 'statusApproved' },
  { id: 'rejected', labelKey: 'statusRejected' },
];

const OWNER_KYC_DOC_LINKS = [
  { docType: 'pan', urlKey: 'pan_url', labelKey: 'kycPan' },
  { docType: 'aadhaar_front', urlKey: 'aadhaar_front_url', labelKey: 'kycAadhaarFront' },
  { docType: 'aadhaar_back', urlKey: 'aadhaar_back_url', labelKey: 'kycAadhaarBack' },
  { docType: 'selfie', urlKey: 'selfie_url', labelKey: 'kycSelfie' },
];

async function parseApiError(res) {
  try {
    const data = await res.json();
    return data?.error || data?.message || `Request failed (${res.status})`;
  } catch {
    return `Request failed (${res.status})`;
  }
}

function formatSubmittedAt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function kycStatusBadgeVariant(status) {
  if (status === 'approved') {
    return { className: 'border-emerald-500/50 bg-emerald-500/15 text-emerald-400' };
  }
  if (status === 'pending') {
    return { className: 'border-amber-500/50 bg-amber-500/15 text-amber-400' };
  }
  if (status === 'rejected') {
    return { className: 'border-red-500/50 bg-red-500/15 text-red-400' };
  }
  return { className: 'border-slate-500/40 bg-slate-500/10 text-slate-400' };
}

function kycStatusLabel(status, t) {
  if (status === 'approved') return t('statusApproved');
  if (status === 'pending') return t('statusPending');
  if (status === 'rejected') return t('statusRejected');
  if (status === 'not_submitted') return t('kycNotSubmitted');
  return status || '—';
}

export default function AdminOwnerKycPanel({ authToken, onQueueChange }) {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [actionId, setActionId] = useState(null);
  const [expandedReviewId, setExpandedReviewId] = useState(null);

  const load = useCallback(async () => {
    if (!authToken) {
      setItems([]);
      setError(t('failedToLoad'));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const qs = params.toString();
      const res = await fetch(
        `${API_BASE_URL}/api/admin/kyc/owners${qs ? `?${qs}` : ''}`,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      if (!res.ok) throw new Error(await parseApiError(res));
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      setItems([]);
      setError(err.message || t('failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [authToken, statusFilter, t]);

  useEffect(() => {
    load();
  }, [load]);

  const reviewKyc = async (ownerId, decision, rejectionReason = '') => {
    if (!authToken || !ownerId) return;
    setActionId(ownerId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/kyc/owners/${ownerId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          decision,
          ...(decision === 'rejected' ? { rejection_reason: rejectionReason } : {}),
        }),
      });
      if (!res.ok) throw new Error(await parseApiError(res));
      setExpandedReviewId(null);
      await load();
      if (onQueueChange) onQueueChange();
    } catch (err) {
      alert(err.message || t('genericError'));
    } finally {
      setActionId(null);
    }
  };

  const handleApprove = (ownerId) => {
    reviewKyc(ownerId, 'approved');
  };

  const handleReject = (ownerId) => {
    const reason = window.prompt(t('adminKycRejectReason'));
    if (reason == null) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      alert(t('adminKycRejectReason'));
      return;
    }
    reviewKyc(ownerId, 'rejected', trimmed);
  };

  const handleViewDoc = async (ownerId, docType) => {
    if (!authToken || !ownerId) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/kyc/owners/${ownerId}/docs/${docType}`,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || t('panDocumentUnavailable'));
      }
      const url = data.signedUrl || data.url;
      if (!url) {
        throw new Error(t('panDocumentUnavailable'));
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      alert(err.message || t('panDocumentUnavailable'));
    }
  };

  const renderDocLinks = (row) => {
    const availableDocs = OWNER_KYC_DOC_LINKS.filter((doc) => row[doc.urlKey]);
    if (availableDocs.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-x-2 gap-y-1">
        {availableDocs.map((doc, index) => (
          <React.Fragment key={doc.docType}>
            {index > 0 ? (
              <span className="text-xs text-slate-500" aria-hidden="true">|</span>
            ) : null}
            <button
              type="button"
              onClick={() => handleViewDoc(row.owner_id, doc.docType)}
              className="text-xs font-semibold text-[#C9A84C] underline-offset-2 hover:underline"
            >
              {t(doc.labelKey)}
            </button>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderActionCell = (row) => {
    const ownerId = row.owner_id;
    const status = row.status;
    const busy = actionId === ownerId;

    if (status === 'approved') {
      return (
        <span className="text-sm font-semibold text-emerald-400">
          ✅ {t('kycVerified')}
        </span>
      );
    }

    if (status === 'rejected') {
      if (expandedReviewId !== ownerId) {
        return (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => setExpandedReviewId(ownerId)}
            className="border-de-gold/40 text-de-gold hover:bg-de-gold/10"
          >
            {t('adminKycRereviewBtn')}
          </Button>
        );
      }
    }

    if (status === 'pending' || (status === 'rejected' && expandedReviewId === ownerId)) {
      return (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            disabled={busy}
            onClick={() => handleApprove(ownerId)}
            className="bg-emerald-600/90 text-white hover:bg-emerald-600"
          >
            {t('adminKycApproveBtn')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => handleReject(ownerId)}
            className="border-red-500/40 text-red-400 hover:bg-red-500/10"
          >
            {t('adminKycRejectBtn')}
          </Button>
        </div>
      );
    }

    return <span className="text-slate-500">—</span>;
  };

  return (
    <Card className="mb-6 w-full min-w-0 rounded-2xl border-de-gold/30 bg-gradient-to-br from-[#122a4a]/60 via-[#0a1628]/95 to-[#060e1c] de-glow-gold shadow-2xl">
      <CardHeader className="p-4 pb-2 sm:p-5">
        <CardTitle className="text-base font-semibold text-[#C9A84C]">
          {String.fromCodePoint(0x1FAAA)} {t('adminOwnerKycTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="min-w-0 space-y-3 p-4 pt-0 sm:p-5 sm:pt-0">
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((filter) => (
            <Button
              key={filter.id || 'all'}
              type="button"
              size="sm"
              variant={statusFilter === filter.id ? 'default' : 'outline'}
              onClick={() => setStatusFilter(filter.id)}
              className={
                statusFilter === filter.id
                  ? 'bg-de-gold/20 text-de-gold border-de-gold/50'
                  : 'border-de-gold/30 text-slate-300 hover:bg-de-gold/10'
              }
            >
              {t(filter.labelKey)}
            </Button>
          ))}
        </div>

        {loading && (
          <p className="text-sm text-slate-400">{t('loading')}</p>
        )}

        {!loading && error && (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-red-400">{error}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={load}
              className="border-de-gold/40 text-de-gold hover:bg-de-gold/10"
            >
              {t('retryLabel')}
            </Button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <p className="text-sm text-slate-400">{t('adminOwnerKycNoSubmissions')}</p>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {[
                    t('adminOwnerName'),
                    t('operatorsColPhone'),
                    'Submitted',
                    t('operatorsColStatus'),
                    'Action',
                  ].map((h) => (
                    <TableHead key={h} className="text-de-gold/70">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => {
                  const badge = kycStatusBadgeVariant(row.status);
                  return (
                    <TableRow key={row.id || row.owner_id}>
                      <TableCell className="font-semibold text-slate-200">
                        <div className="space-y-1">
                          <div>{row.name || row.owner_id}</div>
                          {renderDocLinks(row)}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-300">
                        {row.phone || '—'}
                      </TableCell>
                      <TableCell className="text-sm text-slate-300">
                        {formatSubmittedAt(row.submitted_at)}
                      </TableCell>
                      <TableCell>
                        <Badge className={badge.className}>
                          {kycStatusLabel(row.status, t)}
                        </Badge>
                      </TableCell>
                      <TableCell>{renderActionCell(row)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
